package com.banyan.flightviewer.web.components

import com.banyan.flightviewer.state.AppSettings
import com.banyan.flightviewer.web.dom.button
import com.banyan.flightviewer.web.dom.div
import com.banyan.flightviewer.web.dom.onClick
import com.banyan.flightviewer.web.state.WebAppContainer
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLSelectElement
import org.w3c.dom.events.Event

/**
 * Modal settings dialog. Returns a `close` callback so the caller can hide it.
 *
 * Hides the API key fields (server holds those) and the Pixoo section
 * entirely (desktop-only feature).
 */
fun showSettingsDialog(app: WebAppContainer, onClose: () -> Unit) {
    val backdrop = document.createElement("div")
    backdrop.className = "fv-modal-backdrop"
    document.body!!.appendChild(backdrop)

    val current = app.appState.settings.value
    val modal = backdrop.div(className = "fv-modal") {
        val h2 = document.createElement("h2")
        h2.textContent = "Settings"
        appendChild(h2)
    }

    fun row(label: String, render: org.w3c.dom.Element.() -> Unit): org.w3c.dom.Element {
        val r = modal.div(className = "fv-form-row")
        val l = document.createElement("label")
        l.textContent = label
        r.appendChild(l)
        r.render()
        return r
    }

    val sourceSelect = document.createElement("select") as HTMLSelectElement
    listOf("ADS-B Exchange", "Airplanes.live").forEach { src ->
        val opt = document.createElement("option") as org.w3c.dom.HTMLOptionElement
        opt.value = src
        opt.text = src
        sourceSelect.appendChild(opt)
    }
    sourceSelect.value = current.apiSource
    row("Aircraft data source") { appendChild(sourceSelect) }

    val latInput = numberInput(current.lat.toString())
    row("Latitude") { appendChild(latInput) }

    val lonInput = numberInput(current.lon.toString())
    row("Longitude") { appendChild(lonInput) }

    val locateBtn = document.createElement("button") as org.w3c.dom.HTMLButtonElement
    locateBtn.className = "fv-btn fv-btn--ghost"
    locateBtn.textContent = "Use my location"
    locateBtn.addEventListener("click", { _: Event ->
        useGeolocation(latInput, lonInput)
    })
    row("\u00A0") { appendChild(locateBtn) }

    val radiusInput = numberInput(current.radiusKm.toString())
    row("Radius (km)") { appendChild(radiusInput) }

    val intervalInput = numberInput(current.updateIntervalSec.toString())
    row("Update interval (sec)") { appendChild(intervalInput) }

    val distSelect = document.createElement("select") as HTMLSelectElement
    listOf("miles", "km").forEach { u ->
        val o = document.createElement("option") as org.w3c.dom.HTMLOptionElement
        o.value = u; o.text = u; distSelect.appendChild(o)
    }
    distSelect.value = current.distanceUnits
    row("Distance units") { appendChild(distSelect) }

    val altSelect = document.createElement("select") as HTMLSelectElement
    listOf("ft", "m").forEach { u ->
        val o = document.createElement("option") as org.w3c.dom.HTMLOptionElement
        o.value = u; o.text = u; altSelect.appendChild(o)
    }
    altSelect.value = current.altitudeUnits
    row("Altitude units") { appendChild(altSelect) }

    val speedSelect = document.createElement("select") as HTMLSelectElement
    listOf("kts", "kmh", "mph").forEach { u ->
        val o = document.createElement("option") as org.w3c.dom.HTMLOptionElement
        o.value = u; o.text = u; speedSelect.appendChild(o)
    }
    speedSelect.value = current.speedUnits
    row("Speed units") { appendChild(speedSelect) }

    val enrichmentCb = document.createElement("input") as HTMLInputElement
    enrichmentCb.type = "checkbox"
    enrichmentCb.checked = current.fr24BulkEnrichment
    row("FR24 bulk enrichment") { appendChild(enrichmentCb) }

    // Cooldown between FR24 enrichment batches. Clamp to a sane range so
    // setting 0 or a million doesn't break the monitoring loop.
    val cooldownInput = numberInput(current.enrichmentCooldownSec.toString())
    row("Enrichment cooldown (sec)") { appendChild(cooldownInput) }

    val actions = modal.div(className = "fv-form-actions")
    val cancelBtn = actions.button(className = "fv-btn fv-btn--ghost") {
        textContent = "Cancel"
        onClick {
            backdrop.remove()
            onClose()
        }
    }
    val saveBtn = actions.button(className = "fv-btn fv-btn--primary") {
        textContent = "Save"
        onClick {
            val updated = current.copy(
                apiSource = sourceSelect.value,
                lat = latInput.value.toDoubleOrNull() ?: current.lat,
                lon = lonInput.value.toDoubleOrNull() ?: current.lon,
                radiusKm = radiusInput.value.toIntOrNull() ?: current.radiusKm,
                updateIntervalSec = intervalInput.value.toIntOrNull() ?: current.updateIntervalSec,
                distanceUnits = distSelect.value,
                altitudeUnits = altSelect.value,
                speedUnits = speedSelect.value,
                fr24BulkEnrichment = enrichmentCb.checked,
                enrichmentCooldownSec = cooldownInput.value.toIntOrNull()?.coerceIn(10, 3600)
                    ?: current.enrichmentCooldownSec,
            )
            app.settingsManager.saveSettings(updated)
            app.appState.requestImmediateRefresh()
            backdrop.remove()
            onClose()
        }
    }

    // Backdrop click closes. Modal click swallows.
    backdrop.addEventListener("click", { ev: Event ->
        if (ev.target == backdrop) {
            backdrop.remove()
            onClose()
        }
    })
}

private fun numberInput(initial: String): HTMLInputElement {
    val input = document.createElement("input") as HTMLInputElement
    input.type = "text"
    input.value = initial
    return input
}

private fun useGeolocation(latInput: HTMLInputElement, lonInput: HTMLInputElement) {
    // org.w3c.dom.Navigator's geolocation property isn't exposed in the
    // legacy bindings; reach for it via dynamic dispatch instead.
    val geolocation = window.navigator.asDynamic().geolocation
    if (geolocation == null) {
        window.alert("Geolocation isn't available in this browser.")
        return
    }
    geolocation.getCurrentPosition(
        { position: dynamic ->
            latInput.value = position.coords.latitude.toFixed(4) as String
            lonInput.value = position.coords.longitude.toFixed(4) as String
            Unit
        },
        { err: dynamic ->
            window.alert("Couldn't get location: ${err.message}")
            Unit
        },
    )
}
